import React, { useEffect, useState } from "react";
import {
  Grid,
  Container,
  Card,
  Button,
  Typography,
  CardContent,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { getAccessToken, useAuth } from "../../context/auth";
import Header, { HeaderType } from "../../components/Header";
import { ViewAccidentDetail } from "../../services/api/accident-service";
import { useConfirm } from "@/components/dialog/confirm";
import BrandLoader from "@/branding/BrandLoader";

interface ViewAccidentDetailFormProps {
  style?: any;
  loading: boolean;
}

interface Accident {
  supportNumber?: string;
  status?: string;
  registrationPolicyNumber?: string;
  registrationFirstName?: string;
  registrationLastName?: string;
  registrationMobileNumber?: string;
  registrationIdNumber?: string;
  vehicle?: Vehicle;
  formType?: string;
  createdAt?: string;
}

interface Vehicle {
  licensePlateNumber?: string;
  make?: string;
  model?: string;
  color?: string;
  vehicleDocuments?: string;
  createdAt?: string;
}

const ViewAccidentDetailForm: React.FC<ViewAccidentDetailFormProps> = (
  _props
) => {
  const navigate = useNavigate();
  const { authTokens } = useAuth();
  const token = getAccessToken(authTokens) ?? "";
  const confirm = useConfirm();
  const [accident, setAccident] = useState<Accident>({});
  const [vehicle, setVehicle] = useState<Vehicle>({});
  let [loading, setLoading] = useState(false);
  const { tenant } = useParams<{ tenant: string }>();

  const cancel = () => {
    navigate(`/${tenant}/accidentsupportlist`);
  };

  const showDialog = (title: string, message: string) => {
    confirm({
      catchOnCancel: false,
      title: title,
      description: message,
    });
  };

  const fetchAccident = async () => {
    try {
      setLoading(true);
      var viewAccidentSupportID = JSON.parse(
        window.localStorage.getItem("viewAccidentSupportID") || ""
      );
      const response = await ViewAccidentDetail(token, viewAccidentSupportID);
      if (response) {
        setAccident(response.data);
        setVehicle(response.data["vehicle"]);
        setLoading(false);
      } else {
        setLoading(false);
        showDialog(
          "Failure",
          "There seems to be a problem with your internet connection.\nPlease check your internet connection."
        );
      }
    } catch (err) {
      setLoading(false);
      showDialog(
        "Failure",
        "An exception occurred, please contact your administrator."
      );
      if (err instanceof Error) {
        console.error(err.message);
      }
    }
  };

  useEffect(() => {
    fetchAccident();
  }, []);

  const StyleStatus = (status: string) => {
    if (status === "Pending") {
      return <span style={{ color: "orange" }}>{status}</span>;
    }
    if (status === "Approved") {
      return <span style={{ color: "green" }}>{status}</span>;
    }
    if (status === "Rejected") {
      return <span style={{ color: "red" }}>{status}</span>;
    }
    return <span>{status}</span>;
  };

  // Robust date formatter: accepts ISO string or epoch (ms/s)
  function formatDate(value?: string | number | null): string {
    if (value == null || value === "") return "-";

    let d: Date | null = null;

    if (typeof value === "number") {
      // If value looks like seconds (10 digits), convert to ms
      d = new Date(value < 1e12 ? value * 1000 : value);
    } else if (typeof value === "string") {
      const n = Number(value);
      if (!Number.isNaN(n) && value.trim() !== "") {
        // numeric string -> treat as epoch
        d = new Date(n < 1e12 ? n * 1000 : n);
      } else {
        // ISO or other date-like string
        const parsed = new Date(value);
        if (!isNaN(parsed.getTime())) d = parsed;
      }
    }

    if (!d || isNaN(d.getTime())) return "-";

    // Adjust options to your preference
    return new Intl.DateTimeFormat("en-ZA", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
  }

  return (
    <div>
      <Header type={HeaderType.AccidentSupportView} />
      <Container maxWidth="sm">
        <Grid container spacing={1}>
          <Grid size={12}>
            <Card variant="outlined" style={{ padding: "15px" }}>
              <Grid
                container
                direction="row"
                justifyContent="center"
                spacing={1}
              >
                <Grid size={12}>
                  <Card
                    variant="outlined"
                    style={{ padding: "15px", background: "lightgrey" }}
                  >
                    <Grid
                      container
                      justifyContent="center"
                      alignItems="center"
                      direction="column"
                    >
                      <Typography variant="subtitle1">
                        <strong> Form Details</strong>
                      </Typography>
                    </Grid>
                  </Card>
                  <Grid size={12}>
                    <Card variant="outlined" style={{ padding: "15px" }}>
                      <CardContent>
                        <Typography align="center" variant="body1">
                          <strong>Request Number :</strong>{" "}
                          {accident["supportNumber"]}
                        </Typography>
                        <br />
                        <Typography align="center" variant="body1">
                          <strong>Status :</strong>{" "}
                          {StyleStatus(accident["status"] || "")}
                        </Typography>
                        <br />
                        <Typography align="center" variant="body1">
                          <strong>Policy Number :</strong>{" "}
                          {accident["registrationPolicyNumber"]}
                        </Typography>
                        <br />
                        <Typography align="center" variant="body1">
                          <strong>Date Submitted :</strong>{" "}
                          {formatDate(accident["createdAt"])}
                        </Typography>
                        <br />
                      </CardContent>
                    </Card>
                    <br />
                    <Card
                      variant="outlined"
                      style={{ padding: "15px", background: "lightgrey" }}
                    >
                      <Grid
                        container
                        justifyContent="center"
                        alignItems="center"
                        direction="column"
                      >
                        <Typography variant="subtitle1">
                          <strong> Personal Details</strong>
                        </Typography>
                      </Grid>
                    </Card>
                    <Card variant="outlined" style={{ padding: "15px" }}>
                      <CardContent>
                        <Typography align="center" variant="body1">
                          <strong>First Name :</strong>{" "}
                          {accident["registrationFirstName"]}
                        </Typography>
                        <br />
                        <Typography align="center" variant="body1">
                          <strong>Last Name :</strong>{" "}
                          {accident["registrationLastName"]}
                        </Typography>
                        <br />
                        <Typography align="center" variant="body1">
                          <strong>Mobile Number :</strong>{" "}
                          {accident["registrationMobileNumber"]}
                        </Typography>
                        <br />
                        <Typography align="center" variant="body1">
                          <strong>ID Number :</strong>{" "}
                          {accident["registrationIdNumber"]}
                        </Typography>
                        <br />
                        <Typography align="center" variant="body1">
                          <strong>Policy Number :</strong>{" "}
                          {accident["registrationPolicyNumber"]}
                        </Typography>
                        <br />
                      </CardContent>
                    </Card>
                    {accident["formType"] === "AccidentSupport" && (
                      <>
                        <br />
                        <Card
                          variant="outlined"
                          style={{ padding: "15px", background: "lightgrey" }}
                        >
                          <Grid
                            container
                            justifyContent="center"
                            alignItems="center"
                            direction="column"
                          >
                            <Typography variant="subtitle1">
                              <strong> Vehicle Details</strong>
                            </Typography>
                          </Grid>
                        </Card>

                        <Grid size={12}>
                          <Card variant="outlined" style={{ padding: 15 }}>
                            <CardContent>
                              <Typography align="center" variant="body1">
                                <strong>License Plate Number :</strong>{" "}
                                {vehicle?.licensePlateNumber ?? "-"}
                              </Typography>
                              <br />
                              <Typography align="center" variant="body1">
                                <strong>Make :</strong> {vehicle?.make ?? "-"}
                              </Typography>
                              <br />
                              <Typography align="center" variant="body1">
                                <strong>Model :</strong> {vehicle?.model ?? "-"}
                              </Typography>
                              <br />
                              <Typography align="center" variant="body1">
                                <strong>Color :</strong> {vehicle?.color ?? "-"}
                              </Typography>
                              {/* <br />
                              <Typography align="center" variant="body1">
                                <strong>License Disk :</strong>{" "}
                                {vehicle?.vehicleDocuments ?? "-"}
                              </Typography> */}
                              <br />
                            </CardContent>
                          </Card>
                        </Grid>
                      </>
                    )}
                  </Grid>
                  <Grid size={12}>
                    <br />
                    <Button
                      onClick={cancel}
                      value="cancel"
                      variant="outlined"
                      color="secondary"
                      fullWidth
                      size="large"
                    >
                      <strong>Back</strong>
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </Container>
      <BrandLoader open={loading} />
    </div>
  );
};
export default ViewAccidentDetailForm;
